import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Avatar, Divider, IconButton, useMediaQuery, useTheme, Chip, Tooltip
} from '@mui/material';
import {
  Dashboard, People, TrendingUp, Business, SmartToy,
  ShoppingBag, AttachMoney, Notifications, Article,
  Api, Memory, Menu as MenuIcon, Logout, ChevronLeft,
  Analytics, MoreVert, ChevronRight, Translate, InsertChart, Biotech
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const ADMIN_LINKS = [
  { label: 'Dashboard', icon: <Dashboard fontSize="small" />, path: '/admin' },
  { label: 'Farmers', icon: <People fontSize="small" />, path: '/admin/farmers' },
  { label: 'Prices', icon: <TrendingUp fontSize="small" />, path: '/admin/prices' },
  { label: 'Companies', icon: <Business fontSize="small" />, path: '/admin/companies' },
  { label: 'AI Monitor', icon: <SmartToy fontSize="small" />, path: '/admin/ai' },
  { label: 'AI Analytics', icon: <Analytics fontSize="small" />, path: '/admin/ai-analytics' },
  { label: 'Disease Review', icon: <Biotech fontSize="small" />, path: '/admin/disease-review' },
  { label: 'Analytics', icon: <InsertChart fontSize="small" />, path: '/admin/analytics' },
  { label: 'Marketplace', icon: <ShoppingBag fontSize="small" />, path: '/admin/marketplace' },
  { label: 'Revenue', icon: <AttachMoney fontSize="small" />, path: '/admin/revenue' },
  { label: 'Notifications', icon: <Notifications fontSize="small" />, path: '/admin/notifications' },
  { label: 'Content', icon: <Article fontSize="small" />, path: '/admin/content' },
  { label: 'Translations', icon: <Translate fontSize="small" />, path: '/admin/translations' },
  { label: 'API Logs', icon: <Api fontSize="small" />, path: '/admin/api-logs' },
  { label: 'System', icon: <Memory fontSize="small" />, path: '/admin/system' },
];

const DRAWER_WIDTH = 240;
const DRAWER_COLLAPSED = 64;

const AdminLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile, logout } = useAuth();

  const isActive = (path) =>
    location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path));

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const drawerWidth = isMobile ? DRAWER_WIDTH : (collapsed ? DRAWER_COLLAPSED : DRAWER_WIDTH);

  const NavItem = ({ link }) => {
    const active = isActive(link.path);
    const btn = (
      <ListItemButton
        onClick={() => { navigate(link.path); if (isMobile) setDrawerOpen(false); }}
        sx={{
          borderRadius: '10px',
          mb: 0.3,
          mx: 1,
          minHeight: 42,
          justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
          px: collapsed && !isMobile ? 1.5 : 1.5,
          bgcolor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
          color: active ? '#fff' : 'rgba(255,255,255,0.72)',
          borderLeft: active ? '3px solid rgba(255,255,255,0.7)' : '3px solid transparent',
          '&:hover': {
            bgcolor: 'rgba(255,255,255,0.1)',
            color: '#fff',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <ListItemIcon
          sx={{
            color: 'inherit',
            minWidth: collapsed && !isMobile ? 0 : 34,
            transition: 'all 0.2s',
          }}
        >
          {link.icon}
        </ListItemIcon>
        {(!collapsed || isMobile) && (
          <ListItemText
            primary={link.label}
            primaryTypographyProps={{
              fontSize: 13,
              fontWeight: active ? 700 : 500,
              fontFamily: 'Poppins, sans-serif',
            }}
          />
        )}
      </ListItemButton>
    );

    return (collapsed && !isMobile) ? (
      <Tooltip title={link.label} placement="right" arrow>{btn}</Tooltip>
    ) : btn;
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'linear-gradient(180deg, #1B5E20 0%, #2E7D32 50%, #1B5E20 100%)',
        overflowX: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.2,
          minHeight: 72,
          justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
          position: 'relative',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Avatar
          sx={{
            bgcolor: 'rgba(255,255,255,0.18)',
            border: '2px solid rgba(255,255,255,0.4)',
            width: 36,
            height: 36,
            fontSize: 14,
            fontWeight: 700,
            color: '#fff',
            flexShrink: 0,
          }}
        >
          {userProfile?.name?.[0]?.toUpperCase() || 'A'}
        </Avatar>

        {(!collapsed || isMobile) && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: '#fff', fontSize: 13, lineHeight: 1.2, fontFamily: 'Poppins, sans-serif' }}
                noWrap
              >
                {userProfile?.name || 'Admin'}
              </Typography>
              <Chip
                label="Admin"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: '#A5D6A7',
                  fontSize: '9px',
                  height: 16,
                  fontWeight: 700,
                  fontFamily: 'Poppins, sans-serif',
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, fontFamily: 'Poppins, sans-serif' }}
              noWrap
            >
              FassalkartAI Control
            </Typography>
          </Box>
        )}

        {/* 3-dot toggle — desktop only */}
        {!isMobile && (
          <Tooltip title={collapsed ? 'Expand' : 'Collapse'} placement="right" arrow>
            <IconButton
              size="small"
              onClick={() => setCollapsed(p => !p)}
              sx={{
                color: 'rgba(255,255,255,0.8)',
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                width: 26,
                height: 26,
                flexShrink: 0,
                ml: collapsed ? 0 : 'auto',
              }}
            >
              {collapsed
                ? <ChevronRight sx={{ fontSize: 16 }} />
                : <MoreVert sx={{ fontSize: 16 }} />
              }
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Logo */}
      {(!collapsed || isMobile) && (
        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box component="img" src="/logo192.png" alt="FassalkartAI" sx={{ width: 20, height: 20, objectFit: 'contain' }} />
          <Typography
            sx={{
              color: '#A5D6A7',
              fontSize: '11px',
              fontWeight: 800,
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
              fontFamily: 'Poppins, sans-serif',
            }}
          >
            FassalkartAI
          </Typography>
        </Box>
      )}

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 1 }} />

      {/* Navigation */}
      <List sx={{ flex: 1, py: 0.5 }}>
        {ADMIN_LINKS.map(link => (
          <NavItem key={link.path} link={link} />
        ))}
      </List>

      {/* Logout */}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
      <Box sx={{ p: 1 }}>
        <Tooltip title={collapsed && !isMobile ? 'Sign Out' : ''} placement="right" arrow>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: '10px',
              mx: 0.5,
              color: 'rgba(255,255,255,0.6)',
              justifyContent: collapsed && !isMobile ? 'center' : 'flex-start',
              '&:hover': { bgcolor: 'rgba(255,80,80,0.15)', color: '#EF9A9A' },
            }}
          >
            <ListItemIcon
              sx={{ color: 'inherit', minWidth: collapsed && !isMobile ? 0 : 34 }}
            >
              <Logout fontSize="small" />
            </ListItemIcon>
            {(!collapsed || isMobile) && (
              <ListItemText
                primary="Sign Out"
                primaryTypographyProps={{ fontSize: 13, fontWeight: 500, fontFamily: 'Poppins, sans-serif' }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#F4F8F4' }}>
      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          transition: 'width 0.28s cubic-bezier(0.4,0,0.2,1)',
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            border: 'none',
            boxSizing: 'border-box',
            transition: 'width 0.28s cubic-bezier(0.4,0,0.2,1)',
            overflowX: 'hidden',
            boxShadow: '2px 0 16px rgba(0,0,0,0.12)',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Main area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          transition: 'all 0.28s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* Top bar */}
        <Box
          sx={{
            height: 56,
            bgcolor: '#fff',
            borderBottom: '1px solid rgba(46,125,50,0.1)',
            display: 'flex',
            alignItems: 'center',
            px: 2,
            gap: 2,
            position: 'sticky',
            top: 0,
            zIndex: 100,
            boxShadow: '0 1px 8px rgba(0,0,0,0.04)',
          }}
        >
          {isMobile && (
            <IconButton
              onClick={() => setDrawerOpen(p => !p)}
              size="small"
              sx={{ color: '#546E7A', '&:hover': { color: '#2E7D32', bgcolor: 'rgba(46,125,50,0.08)' } }}
            >
              <MenuIcon fontSize="small" />
            </IconButton>
          )}
          <Typography
            variant="subtitle1"
            fontWeight={700}
            color="primary"
            fontFamily="Poppins, sans-serif"
            fontSize={15}
          >
            Admin Panel
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ ml: 'auto', fontFamily: 'Poppins, sans-serif', fontSize: 11 }}
          >
            {new Date().toLocaleDateString('en-IN', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}
          </Typography>
        </Box>

        {/* Page content */}
        <Box sx={{ flex: 1, p: { xs: 2, sm: 2.5, md: 3 }, overflowX: 'hidden' }}>
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default AdminLayout;
